/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import { Search, Calendar, MapPin, Star, ArrowLeft } from 'lucide-react';
import { getCategory, categoryStyles, TEAL_ACCENT } from '../utils/homeHelpers';
import { eventSeatsLeft } from '../utils/communityMembership';

const COMM = `${API}/community`;

const CATEGORIES = ['All', 'Wellness', 'Inclusion', 'Career Dev', 'Community', 'Climate'];
const DAY_FILTERS = [
  { key: 'any', label: 'Any day' },
  { key: 'today', label: 'Today' },
  { key: 'weekend', label: 'This weekend' },
  { key: 'week', label: 'This week' },
];

export default function CommunityAllEventsPage() {
  const navigate = useNavigate();
  const { token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [day, setDay] = useState('any');
  const [near, setNear] = useState(false);
  const [cat, setCat] = useState('All');
  // City filter — default "All"; dynamic list derived from events below.
  const [city, setCity] = useState('All');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${COMM}/events`, authHeaders);
        if (!cancelled) setEvents(res.data || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [token]);

  // Build the city chip list dynamically from the actual events in the DB.
  // De-dupe case-insensitively but preserve the original casing of the first
  // occurrence so chips render exactly as the data does. Sorted alphabetically
  // after the leading "All" chip.
  const cities = useMemo(() => {
    const seen = new Map(); // key=lowercased, value=original
    (events || []).forEach(ev => {
      const c = (ev.city || '').trim();
      if (!c) return;
      const k = c.toLowerCase();
      if (!seen.has(k)) seen.set(k, c);
    });
    return ['All', ...Array.from(seen.values()).sort((a, b) => a.localeCompare(b))];
  }, [events]);

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day0 = today.getDay();           // 0=Sun..6=Sat
    const daysToSat = (6 - day0 + 7) % 7;  // next Saturday
    return (events || []).filter(ev => {
      // Text search across title + description + location.
      if (q.trim()) {
        const needle = q.trim().toLowerCase();
        const hay = `${ev.title || ''} ${ev.description || ''} ${ev.location || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      // Category filter — re-uses the shared getCategory() so labels match the home shelf.
      if (cat !== 'All') {
        const evCat = getCategory(ev.title, ev.category);
        if (!evCat.startsWith(cat)) return false;
      }
      // Day filter
      if (day !== 'any' && ev.event_date) {
        const d = new Date(ev.event_date);
        d.setHours(0, 0, 0, 0);
        const diff = (d - today) / (1000 * 60 * 60 * 24);
        if (day === 'today' && diff !== 0) return false;
        if (day === 'week' && (diff < 0 || diff > 7)) return false;
        if (day === 'weekend') {
          // Saturday or Sunday within the next 7 days.
          if (diff < daysToSat || diff > daysToSat + 1) return false;
        }
      }
      // City filter
      if (city !== 'All') {
        if (!ev.city || ev.city.toLowerCase() !== city.toLowerCase()) return false;
      }
      // Near-me — only filter if the event has a numeric `distance_km`.
      if (near && typeof ev.distance_km === 'number' && ev.distance_km > 10) return false;
      return true;
    });
  }, [events, q, cat, day, near, city]);

  return (
    <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: 80 }}>
      <CommunityTopHeader />
      <div style={{ width: '100%', maxWidth: 720, margin: '0 auto', padding: '0 16px' }}>
        {/* Back row */}
        <button type="button" onClick={() => navigate('/community')}
          data-testid="all-events-back-btn"
          style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', margin: '12px 0 4px', padding: 0 }}>
          <ArrowLeft size={14} /> Back
        </button>

        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', margin: '6px 0 14px' }}>All Events</h1>

        {/* City filter — sticky chip strip that stays visible while scrolling.
            List is populated dynamically from event.city values in the DB; if
            no events carry a city we only render the "All" chip. */}
        {cities.length > 1 && (
          <div
            data-testid="city-filter-bar"
            style={{
              position: 'sticky', top: 0, zIndex: 5,
              background: '#F5F7FA',
              padding: '8px 0 10px',
              margin: '0 -16px 12px',
              borderBottom: '1px solid #EEF1F4',
            }}
          >
            <div
              style={{
                display: 'flex', gap: 8, overflowX: 'auto',
                padding: '2px 16px',
                scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
              }}
              className="hide-scrollbar"
            >
              {cities.map(c => {
                const active = city === c;
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`city-chip-${c}`}
                    onClick={() => setCity(c)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 14px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? TEAL_ACCENT : '#E5E7EB'}`,
                      fontSize: 12,
                      fontWeight: 600,
                      background: active ? `${TEAL_ACCENT}15` : '#FFFFFF',
                      color: active ? TEAL_ACCENT : '#374151',
                      cursor: 'pointer',
                      scrollSnapAlign: 'start',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} color="#9CA3AF" style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)' }} />
          <input data-testid="all-events-search" type="search" placeholder="Search events"
            value={q} onChange={e => setQ(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px 11px 36px', borderRadius: 12, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', background: '#FFFFFF' }} />
        </div>

        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 12 }} className="hide-scrollbar">
          {/* Day */}
          <select data-testid="filter-day" value={day} onChange={e => setDay(e.target.value)}
            style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 999, border: `1.5px solid ${day !== 'any' ? TEAL_ACCENT : '#E5E7EB'}`, fontSize: 12, fontWeight: 600, background: day !== 'any' ? `${TEAL_ACCENT}15` : '#FFFFFF', color: day !== 'any' ? TEAL_ACCENT : '#374151', cursor: 'pointer' }}>
            {DAY_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
          {/* Near me */}
          <button type="button" data-testid="filter-near"
            onClick={() => setNear(n => !n)}
            style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 999, border: `1.5px solid ${near ? TEAL_ACCENT : '#E5E7EB'}`, fontSize: 12, fontWeight: 600, background: near ? `${TEAL_ACCENT}15` : '#FFFFFF', color: near ? TEAL_ACCENT : '#374151', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={12} /> Near me
          </button>
          {/* Categories */}
          {CATEGORIES.map(c => (
            <button key={c} type="button" data-testid={`filter-cat-${c}`}
              onClick={() => setCat(c)}
              style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 999, border: `1.5px solid ${cat === c ? TEAL_ACCENT : '#E5E7EB'}`, fontSize: 12, fontWeight: 600, background: cat === c ? `${TEAL_ACCENT}15` : '#FFFFFF', color: cat === c ? TEAL_ACCENT : '#374151', cursor: 'pointer' }}>
              {c}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>Loading events…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>No events match your filters.</div>
        ) : (
          <div data-testid="all-events-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {filtered.map(ev => {
              const c = getCategory(ev.title, ev.category);
              const cs = categoryStyles[c] || categoryStyles.Wellness;
              const reward = ev.reward_stars || ev.reward_points || 30;
              const seatsLeft = eventSeatsLeft(ev);
              return (
                <div key={ev.id} data-testid={`all-events-card-${ev.id}`}
                  onClick={() => navigate(`/community/event-detail/${ev.id}`, { state: { event: ev } })}
                  style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
                    background: ev.image_url ? `url(${ev.image_url}) center/cover` : `linear-gradient(135deg, ${TEAL_ACCENT}, #7DD3C0)`,
                    height: 170,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 60%)' }} />
                  <span style={{ position: 'absolute', top: 8, left: 8, padding: '3px 8px', borderRadius: 10, fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', background: cs.bg, color: cs.color }}>{c}</span>
                  {seatsLeft != null && (
                    <span data-testid={`all-events-seats-${ev.id}`} style={{ position: 'absolute', top: 8, right: 8, padding: '3px 8px', borderRadius: 10, fontSize: 9, fontWeight: 700, background: seatsLeft === 0 ? '#FEE2E2' : 'rgba(255,255,255,0.9)', color: seatsLeft === 0 ? '#B91C1C' : '#1F2937' }}>
                      {seatsLeft === 0 ? 'Full' : `${seatsLeft} left`}
                    </span>
                  )}
                  <div style={{ position: 'absolute', left: 10, right: 10, bottom: 28 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ev.title}</div>
                    <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'rgba(255,255,255,0.85)', marginTop: 3 }}>
                      {ev.event_date && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Calendar size={10} />{new Date(ev.event_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>}
                      {ev.distance_km != null && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><MapPin size={10} />{ev.distance_km} km</span>}
                    </div>
                  </div>
                  {/* Reward strip */}
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '5px 10px', background: 'linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#7C2D12', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Star size={10} fill="#7C2D12" color="#7C2D12" /> Earn {reward}
                    </span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#7C2D12' }}>Redeem &rarr;</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <CommunityBottomNav active="home" />
    </div>
  );
}
